"use client";

import { useState, useEffect, useRef } from "react";

const ANIMATION_DURATION_MS = 2000;

interface AnimatedDigitProps {
  digit: string;
  className?: string;
}

export function AnimatedDigit({ digit, className }: AnimatedDigitProps) {
  const [animatedValue, setAnimatedValue] = useState<string>("0");
  const [hasAnimated, setHasAnimated] = useState(false);
  const elementRef = useRef<HTMLSpanElement>(null);

  const numericValue = parseFloat(digit);
  const isNumeric = !isNaN(numericValue) && isFinite(numericValue);
  const isInteger = Number.isInteger(numericValue);
  const decimalPlaces = isNumeric && !isInteger 
    ? (digit.split(".")[1]?.length || 0) 
    : 0;

  const displayValue = isNumeric ? animatedValue : digit;

  useEffect(() => {
    if (!isNumeric || hasAnimated || !elementRef.current) {
      return;
    }

    const animateCounter = (targetValue: number) => {
      const duration = ANIMATION_DURATION_MS;
      const startTime = performance.now();
      const startValue = 0;

      const animate = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);

        const easeOutQuart = 1 - Math.pow(1 - progress, 4);
        const currentValue = startValue + (targetValue - startValue) * easeOutQuart;

        if (isInteger) {
          setAnimatedValue(Math.floor(currentValue).toString());
        } else {
          setAnimatedValue(currentValue.toFixed(decimalPlaces));
        }

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          if (isInteger) {
            setAnimatedValue(targetValue.toString());
          } else {
            setAnimatedValue(targetValue.toFixed(decimalPlaces));
          }
        }
      };

      requestAnimationFrame(animate);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated) {
            setHasAnimated(true);
            animateCounter(numericValue);
          }
        });
      },
      { threshold: 0.1 }
    );

    observer.observe(elementRef.current);

    return () => {
      observer.disconnect();
    };
  }, [isNumeric, hasAnimated, numericValue, digit, isInteger, decimalPlaces]);

  return (
    <span
      ref={elementRef}
      className={className ?? "font-sans text-factoid-sm font-bold md:text-factoid"}
    >
      {displayValue}
    </span>
  );
}
